// Pure helpers for the upload→dispatch failure path (orphan prevention) and
// for offering a recoverable orphan a "Try again" on the meeting page.
// See docs/superpowers/specs/2026-06-08-orphan-prevention-retry-design.md.

export type UploadCleanup = "mark_failed" | "rollback" | "none";

/**
 * Decide what to do with a half-finished upload when handleSubmit throws.
 * - audio already in R2  -> mark_failed: keep the row, mark it errored, offer
 *   a durable "Try again" (no re-upload).
 * - row created but audio never uploaded -> rollback: the row is worthless,
 *   delete it; the user retries from scratch.
 * - no row was created -> none.
 */
export function decideUploadCleanup(args: {
  rowCreated: boolean;
  audioInR2: boolean;
}): UploadCleanup {
  if (args.audioInR2) return "mark_failed";
  if (args.rowCreated) return "rollback";
  return "none";
}

/**
 * A "recoverable orphan" is a meeting whose upload finished but whose
 * processing never started: it failed (status 'error'), its audio is in R2,
 * and it has NO transcript row (the worker creates that row, so its absence
 * means Soniox never ran). A worker error that already produced a transcript
 * has a non-null transcript and must keep the destructive Danger-Zone path,
 * since resubmit would discard that real transcript.
 */
export function isRecoverableOrphan(
  meeting: { status: string; r2_object_key: string | null },
  transcript: unknown | null,
): boolean {
  return meeting.status === "error" && !!meeting.r2_object_key && transcript === null;
}
